import React, { useEffect, useState } from 'react'
import {
	Container,
	Box,
	Typography,
	Paper,
	TextField,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Stack,
	Chip,
	Divider,
	IconButton,
} from '@mui/material'
import { Add, Delete, Edit, UploadFile, Close } from '@mui/icons-material'

const LS_KEY = 'topLists'

// Helper function to create unique key from top and name
function createTopListKey(top, name) {
	const sanitizedName = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '-')

	return `${top}-${sanitizedName}`
}

export function loadTopListsFromLocalStorage() {
	try {
		const raw = localStorage.getItem(LS_KEY)
		if (!raw) return []
		const parsed = JSON.parse(raw)
		if (!Array.isArray(parsed)) return []

		// Add key property to each item if it doesn't exist
		const withKeys = parsed.map((item) => ({
			...item,
			key: item.key || createTopListKey(item.top, item.name),
		}))

		// Sort first by top ascending, then by name ascending
		withKeys.sort((a, b) => {
			if ((a.top || 0) !== (b.top || 0)) return (a.top || 0) - (b.top || 0)
			const nameA = (a.name || '').toLowerCase()
			const nameB = (b.name || '').toLowerCase()

			return nameA.localeCompare(nameB)
		})

		return withKeys
	} catch (e) {
		console.error('Failed to parse top lists from localStorage', e)

		return []
	}
}

function saveToLocalStorage(items) {
	localStorage.setItem(LS_KEY, JSON.stringify(items))
}

function makeEmptyTopList() {
	return {
		id: String(Date.now()),
		name: '',
		top: 10,
		descriptions: [],
		values: [],
		key: '', // Will be set when saved
	}
}

// Convert values array to CSV format
function valuesToCSV(values) {
	if (!Array.isArray(values) || values.length === 0) return ''

	return values
		.map((item) => {
			const rank = item.rank || ''
			const name = item.name || ''
			const value = item.value !== undefined ? item.value : ''
			// If we have all three fields, use rank,name,value format
			if (rank && name && value !== '') {
				return `${rank},${name},${value}`
			}
			// If we have name and value, use name,value format
			else if (name && value !== '') {
				return `${name},${value}`
			}
			// Otherwise just the name
			else {
				return name
			}
		})
		.join('\n')
}

export default function Settings({ onClose }) {
	const [state, setState] = useState({
		items: loadTopListsFromLocalStorage(),
		editing: null,
		dialogOpen: false,
		form: makeEmptyTopList(),
		newDescription: '',
		valuesInput: '',
	})

	useEffect(() => {
		saveToLocalStorage(state.items)
	}, [state.items])

	const set = (patch) => setState((prev) => ({ ...prev, ...patch }))

	const openNew = () => {
		set({
			form: makeEmptyTopList(),
			valuesInput: '',
			newDescription: '',
			editing: null,
			dialogOpen: true,
		})
	}

	const openEdit = (item) => {
		set({
			form: {
				id: item.id || String(Date.now()),
				name: item.name || '',
				top: typeof item.top !== 'undefined' ? item.top : 10,
				descriptions: Array.isArray(item.descriptions) ? [...item.descriptions] : [],
				values: Array.isArray(item.values) ? [...item.values] : [],
				key: item.key || createTopListKey(item.top, item.name),
			},
			editing: item.id,
			dialogOpen: true,
			valuesInput: valuesToCSV(item.values || []),
		})
	}

	const closeDialog = () => set({ dialogOpen: false, editing: null })

	const addDescription = () => {
		const v = (state.newDescription || '').trim()
		if (!v) return
		set({
			form: { ...state.form, descriptions: [...(state.form.descriptions || []), v] },
			newDescription: '',
		})
	}

	const removeDescription = (index) => {
		set({
			form: {
				...state.form,
				descriptions: (state.form.descriptions || []).filter((_, i) => i !== index),
			},
		})
	}

	const parseValuesInput = (input) => {
		const raw = (input || '').trim()
		if (!raw) return []

		// Try parse as JSON first
		try {
			let fixedRaw = raw
			if (raw.startsWith('{') && !raw.startsWith('[')) {
				fixedRaw = `[${raw}]`
			}
			const parsedJson = JSON.parse(fixedRaw)
			if (Array.isArray(parsedJson)) {
				return parsedJson
					.filter((p) => p && p.name)
					.map((p, i) => ({
						rank: typeof p.rank !== 'undefined' ? p.rank : i + 1,
						name: p.name || '',
						value: p.value ? Number(p.value) : undefined,
					}))
			} else if (parsedJson && typeof parsedJson === 'object' && parsedJson.name) {
				return [
					{
						rank: typeof parsedJson.rank !== 'undefined' ? parsedJson.rank : 1,
						name: parsedJson.name || '',
						value:
							typeof parsedJson.value !== 'undefined'
								? Number(parsedJson.value)
								: undefined,
					},
				]
			}
		} catch (e) {
			// fallback to CSV parsing
		}

		// Parse as CSV
		const lines = raw
			.split(/\r?\n/)
			.map((l) => l.trim())
			.filter(Boolean)

		const parsed = []
		let nextRank = 1

		for (const line of lines) {
			const parts = line.split(',').map((p) => p.trim())

			if (parts.length === 1) {
				// Just name
				parsed.push({ rank: nextRank, name: parts[0], value: undefined })
				nextRank += 1
			} else if (parts.length === 2) {
				// name,value or rank,name
				const firstIsNumber = !isNaN(Number(parts[0]))
				if (firstIsNumber && isNaN(Number(parts[1]))) {
					// rank,name
					parsed.push({
						rank: Number(parts[0]),
						name: parts[1],
						value: undefined,
					})
				} else {
					// name,value
					parsed.push({
						rank: nextRank,
						name: parts[0],
						value:
							parts[1] === '' ? undefined : Number(parts[1].replace(/[^0-9.-]/g, '')),
					})
				}
				nextRank += 1
			} else if (parts.length >= 3) {
				// rank,name,value
				const rank = Number(parts[0])
				const name = parts.slice(1, -1).join(',')
				const value = Number(parts[parts.length - 1].replace(/[^0-9.-]/g, ''))

				parsed.push({
					rank: !isNaN(rank) ? rank : nextRank,
					name,
					value: !isNaN(value) ? value : undefined,
				})
				nextRank += 1
			}
		}

		return parsed
	}

	const handleSave = () => {
		const form = state.form
		if (!form.name.trim()) {
			alert('Please enter a name')

			return
		}

		const parsedValues = parseValuesInput(state.valuesInput)
		const cleaned = {
			...form,
			name: form.name.trim(),
			top: Number(form.top) || 0,
			values: parsedValues,
			descriptions: Array.isArray(form.descriptions) ? form.descriptions : [],
			id: form.id || String(Date.now()),
			key: createTopListKey(Number(form.top) || 0, form.name.trim()),
		}

		if (state.editing) {
			set({ items: state.items.map((i) => (i.id === state.editing ? cleaned : i)) })
		} else {
			set({ items: [cleaned, ...(state.items || [])] })
		}

		set({ valuesInput: '' })
		closeDialog()
	}

	const handleDelete = (id) => {
		if (!window.confirm('Delete this item?')) return
		set({ items: (state.items || []).filter((i) => i.id !== id) })
	}

	const handleFileUpload = (e) => {
		const file = e.target.files && e.target.files[0]
		if (!file) return

		const reader = new FileReader()
		reader.onload = () => {
			try {
				const text = reader.result

				// Check if it's JSON
				if (file.type === 'application/json' || file.name.endsWith('.json')) {
					const parsed = JSON.parse(text)
					if (Array.isArray(parsed) && parsed.every((p) => p.name)) {
						set({ valuesInput: valuesToCSV(parsed) })
					} else if (parsed && typeof parsed === 'object' && parsed.name) {
						const withId = { ...parsed, id: parsed.id || String(Date.now()) }
						set({
							form: {
								...state.form,
								...withId,
								key: withId.key || createTopListKey(withId.top, withId.name),
							},
							valuesInput: valuesToCSV(withId.values || []),
						})
					} else {
						alert('JSON file format not recognized.')
					}
				} else {
					// Treat as CSV
					set({ valuesInput: text })
				}
			} catch (err) {
				// If JSON parsing fails, treat as CSV
				set({ valuesInput: reader.result })
			}
		}
		reader.readAsText(file)
		e.currentTarget.value = ''
	}

	return (
		<Container maxWidth="lg" sx={{ py: 2, maxHeight: '85vh', overflow: 'auto' }}>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mb: 3, position: 'sticky', top: 0, zIndex: 1 }}
			>
				<Typography variant="h4">TopLists</Typography>
				<Stack direction="row" spacing={1}>
					<Button variant="contained" startIcon={<Add />} onClick={openNew}>
						New TopList
					</Button>
					{onClose && (
						<IconButton onClick={onClose}>
							<Close />
						</IconButton>
					)}
				</Stack>
			</Stack>
			<Stack spacing={2}>
				{(!state.items || state.items.length === 0) && (
					<Paper sx={{ p: 3 }}>
						<Typography>No items found. Click "New TopList" to add one.</Typography>
					</Paper>
				)}
				{(state.items || []).map((it) => (
					<Paper key={it.id} sx={{ p: 2 }}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="flex-start"
						>
							<Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
								<Typography variant="h6">{it.name}</Typography>
								<Typography variant="body2">Top: {it.top}</Typography>
								<Typography variant="caption">
									Values: {(it.values || []).length}
								</Typography>
								<Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{(it.descriptions || []).map((d, idx) => (
										<Chip
											key={idx}
											label={d}
											size="small"
											sx={{
												maxWidth: '100%',
												wordBreak: 'break-word',
												'& .MuiChip-label': {
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												},
											}}
										/>
									))}
								</Box>
							</Box>
							<Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
								<Button
									variant="contained"
									startIcon={<Edit />}
									onClick={() => openEdit(it)}
								>
									Edit
								</Button>
								<Button
									variant="contained"
									color="error"
									startIcon={<Delete />}
									onClick={() => handleDelete(it.id)}
								>
									Delete
								</Button>
							</Stack>
						</Stack>
					</Paper>
				))}
			</Stack>

			<Dialog open={state.dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
				<DialogTitle>{state.editing ? 'Edit TopList' : 'New TopList'}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
							<TextField
								label="Name"
								fullWidth
								value={state.form.name}
								onChange={(e) =>
									set({ form: { ...state.form, name: e.target.value } })
								}
							/>
							<TextField
								label="Top"
								value={state.form.top}
								onChange={(e) =>
									set({ form: { ...state.form, top: Number(e.target.value) } })
								}
								sx={{ width: 140 }}
							/>
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<TextField
								label="New description"
								value={state.newDescription}
								onChange={(e) => set({ newDescription: e.target.value })}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault()
										addDescription()
									}
								}}
								fullWidth
							/>
							<Button variant="contained" onClick={addDescription}>
								Add description
							</Button>
						</Stack>
						<Box>
							<Typography variant="subtitle2">Descriptions</Typography>
							<Box sx={{ mt: 1 }}>
								{(state.form.descriptions || []).map((d, idx) => (
									<Chip
										key={idx}
										label={d}
										onDelete={() => removeDescription(idx)}
										sx={{ mr: 1, mb: 1 }}
									/>
								))}
							</Box>
						</Box>
						<Divider />
						<Box>
							<Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
								Values
							</Typography>
							<Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
								<Button
									variant="contained"
									component="label"
									startIcon={<UploadFile />}
								>
									Upload File
									<input
										hidden
										accept=".json,.csv,.txt"
										type="file"
										onChange={handleFileUpload}
									/>
								</Button>
							</Stack>
							<TextField
								label="Values (CSV format: name,value or rank,name,value)"
								value={state.valuesInput}
								onChange={(e) => set({ valuesInput: e.target.value })}
								multiline
								minRows={4}
								fullWidth
								placeholder="Example:&#10;Item 1,100&#10;Item 2,95&#10;1,Item 3,90"
							/>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" color="secondary" onClick={closeDialog}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleSave}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	)
}
